<template>
	<div>
		<div v-if="$global.hasUserRole(userRoles, 'mobilesims:create') || totalSims > 0" class="stage">
			<page-header title="Mobile SIMs" />

			<filter-bar
				:show-filters="showFilters"
				:has-export="true"
				:show-add-button="$global.hasUserRole(userRoles, 'mobilesims:create')"
				add-button-label="Activate (e)SIM"
				@toggle-filters="showFilters = !showFilters"
				@reset-filters="resetSearchParams"
				@export="exportToCsv"
				@add-new="routeTo('/mobilesims/new')"
			>
			</filter-bar>

			<filter-section
				:show="showFilters"
				:filters="searchParams"
				:search-term="searchedSims"
				:tenants="tenants"
				:accounts="accounts"
				:allow-tenants="true"
				tenant-label="Tenant SIMs"
				show-account-filter
				show-tenant-filter
				show-date-filter
				date-label="Date Added"
				show-search-filter
				search-label="Search"
				@update:filters="searchParams = $event"
				@update:search-term="searchedSims = $event"
				@tenant-change="$global.setSelectedTenant"
			/>

			<data-view
				:view-mode="'table'"
				:columns="columns"
				:sort-column="sortColumn"
				:reverse="reverse"
				:loading="loading"
				:label="'Mobile SIMs'"
				:total-items="totalSims"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="computedPages"
				:page-options="[10, 25, 100]"
				@sort="setSort"
				@update:page-size="changePageSize"
				@page-change="changePage"
			>
				<template #table-view>
					<generic-table
						:items="mobilesims"
						:columns="columns"
						item-type="mobile sims"
						base-route="mobilesims"
						:can-edit="$global.hasUserRole(userRoles, 'mobilesims:update')"
						:view-mode="'table'"
						:empty-state-config="{
							icon: 'fa-light fa-sim-card',
							title: 'No mobile SIMs yet',
							description: 'Add your first mobile SIM to start managing cellular connectivity.',
							action: $global.hasUserRole(userRoles, 'mobilesims:create')
								? {
										label: 'Add Mobile SIM',
										icon: 'fa-light fa-plus',
										handler: () => routeTo('/mobilesims/new')
									}
								: null
						}"
						:actions="[
							{
								handler: (sim) => routeTo(`/mobilesims/${sim.id}`),
								icon: 'fa-light fa-pencil',
								label: 'Edit'
							},
							{
								handler: showQRCode,
								icon: 'fa-light fa-qrcode',
								label: 'View QR Code',
								show: (sim) => sim.type === 'esim'
							},
							{
								handler: showUsage,
								icon: 'fa-light fa-chart-line',
								label: 'View Usage',
								show: (sim) => sim.status === 'Active'
							},
							{
								permission: 'mobilesims:delete',
								handler: deleteSim,
								icon: 'fa-light fa-delete-left',
								label: 'Delete'
							}
						]"
					>
						<template #number="{ item }">
							<span
								class="fa-light fa-fw mr-2"
								:class="
									item.status === 'Active'
										? 'fa-check'
										: item.status === 'Terminated'
											? 'fa-xmark'
											: item.status === 'Suspended'
												? 'fa-pause'
												: 'fa-question'
								"
								@click.stop.prevent="showQRCode(item)"
							>
							</span>
							<span>{{ item.number }}</span>
						</template>
						<template #type="{ item }">
							<span
								:class="
									item.type === 'psim'
										? 'fa-light fa-fw fa-sim-card scale-125'
										: 'fa-light fa-fw fa-qrcode scale-125'
								"
								:title="item.type === 'psim' ? 'Physical SIM' : 'eSIM'"
								@click="item.type === 'esim' && showQRCode(item, $event)"
							>
							</span>
						</template>
						<template #tenant="{ item }">
							{{ item.tenant?.companyName || 'Unassigned' }}
						</template>
						<template #account="{ item }">
							{{ item.account?.name || 'Unassigned' }}
						</template>
						<template #mobile-view="{ item }">
							<div class="flex w-full flex-col">
								<div class="text-md absolute right-4 top-4 block max-w-[160px]">
									<p class="text-right font-semibold uppercase text-gray-400">Account</p>
									<p class="truncate text-right">{{ item.account?.name || 'Unassigned' }}</p>
								</div>

								<div class="flex flex-col gap-2">
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Number:</p>
										<p>{{ item.number }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Tenant:</p>
										<p>{{ item.tenant?.companyName || 'Unassigned' }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Created:</p>
										<p>{{ $global.dateFormat(item.createdAt) }}</p>
									</div>
								</div>
							</div>
						</template>
					</generic-table>
				</template>
			</data-view>
		</div>
		<div v-else class="flex h-[calc(100vh-200px)] w-full justify-center md:items-center">
			<div
				class="max-w-md rounded-lg border border-gray-200 bg-white p-8 shadow-lg dark:border-gray-700 dark:bg-gray-800"
			>
				<div class="text-center">
					<span class="fa-light fa-circle-exclamation mb-4 block text-7xl text-gray-400"></span>
					<p class="text-lg text-gray-600 dark:text-gray-300">
						This feature is not currently active on your account. Please contact your Account Manager for
						further details.
					</p>
				</div>
			</div>
		</div>
		<QRCodeModal v-if="showQRModal" :sim-id="selectedSimId" @close="showQRModal = false" />
		<SIMUsageModal
			v-if="showUsageModal"
			:sim-id="selectedSimId"
			:usage="selectedSimUsage"
			:is-loading="isLoadingUsage"
			@close="showUsageModal = false"
		/>
	</div>
</template>

<script>
import mobilesimsApi from '../scripts/mobilesims/mobilesims.api'
import accountsApi from '../scripts/accounts/accounts.api'
import { router } from '../router'
import moment from 'moment'
import debounce from 'lodash.debounce'
import SIMUsageModal from '../components/SIMUsageModal.vue'

export default {
	components: {
		SIMUsageModal
	},
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			columns: [
				{
					key: 'number',
					label: 'Number',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'iccid',
					label: 'ICCID',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2 hideTablet'
				},
				{
					key: 'type',
					label: 'Type',
					sortable: true,
					class: 'col-xs-12 col-sm-1',
					format: (value) => (!value ? 'Undefined' : value === 'psim' ? 'Physical SIM' : 'eSIM')
				},
				{
					key: 'tenant',
					label: 'Tenant',
					class: 'col-lg-1 col-lg-1 col-sm-1 col-md-1 hideTabletLandscape'
				},
				{
					key: 'account',
					label: 'Account',
					class: 'col-lg-2 col-lg-1 col-sm-2'
				},
				{
					key: 'description',
					label: 'Description',
					class: 'col-xs-12 col-sm-3 col-md-2'
				},
				{
					key: 'createdAt',
					label: 'Date Added',
					sortable: true,
					class: 'col-xs-3 col-lg-2 hideTabletLandscape'
				}
			],
			showFilters: true,
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: '',
				selectedAccount: null
			},
			mobilesims: [],
			totalSims: 0,
			accounts: [],
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'number',
			reverse: false,
			loading: false,
			searchedSims: '',
			showQRModal: false,
			selectedSimId: null,
			showUsageModal: false,
			selectedSimUsage: null,
			isLoadingUsage: false
		}
	},

	computed: {
		computedPages() {
			const numPages = Math.ceil(this.totalSims / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		},
		selectedTenantName() {
			if (!this.searchParams.selectedTenant) return 'All Tenants'
			const tenant = this.tenants?.find((t) => t.id === parseInt(this.searchParams.selectedTenant))
			return tenant?.companyName || 'All Tenants'
		}
	},

	watch: {
		dates: {
			handler: function (val) {
				if (val?.length === 2) {
					this.searchParams.dateAddedFrom = moment(val[0]).format('YYYY-MM-DD')
					this.searchParams.dateAddedTo = moment(val[1]).format('YYYY-MM-DD')
					this.reloadSims()
				}
			},
			deep: true
		},
		searchedSims(...args) {
			this.debouncedWatch(...args)
		},
		searchParams: {
			handler(newValue, oldValue) {
				this.reloadSims()
			},
			deep: true
		},
		currentPage: {
			handler(newValue, oldValue) {
				this.reloadSims()
			}
		},
		'$route.query.globalSearch': {
			handler(newValue, oldValue) {
				if (newValue) this.reloadSims(newValue)
			}
		}
	},

	created() {
		if (this.$global.getSelectedTenant()) this.searchParams.selectedTenant = this.$global.getSelectedTenant()

		accountsApi.getAll({ limit: 2500 }).then((response) => {
			this.accounts = response.data.results
		})

		this.debouncedWatch = debounce((newValue, oldValue) => {
			this.reloadSims()
		}, 1000)
		if (this.$route.query && this.$route.query.globalSearch) this.reloadSims(this.$route.query.globalSearch)
		else this.reloadSims()
	},
	beforeUnmount() {
		this.debouncedWatch.cancel()
		if (this.getRefreshInterval) clearInterval(this.getRefreshInterval)
	},

	methods: {
		resetDates() {
			this.dates = []
			this.searchParams.dateAddedFrom = null
			this.searchParams.dateAddedTo = null
		},
		formatDates(date1, date2) {
			if (date1 && date2) return moment(date1).format('Do MMM YY') + ' to ' + moment(date2).format('Do MMM YY')
			else if (date1) return moment(date1).format('Do MMM YY')
			else return null
		},
		exportToCsv() {
			let searchParams = this.getSearchParams()

			searchParams.exportCsv = true

			axios
				.get(window.dtServerUrl + '/mobilesims', {
					params: searchParams
				})
				.then((response) => {
					let blob = new Blob([response.data], { type: response.headers.get('content-type') })
					// let filename = (response.headers['Content-Disposition'] || '').split('filename=')[1]
					let link = document.createElement('a')
					link.href = window.URL.createObjectURL(blob)
					link.download = 'teleflow-sims.csv'
					link.click()
				})
				.catch((error) => {
					console.log(error)
				})
		},
		setSort(column, reverse) {
			if (this.sortColumn !== column) this.reverse = false
			else this.reverse = reverse

			this.sortColumn = column

			this.reloadSims()
		},
		changePageSize(size) {
			this.pageSize = size
			let numPages = Math.ceil(this.totalSims / size) || 1
			if (numPages < this.currentPage) this.currentPage = numPages
			else this.reloadSims()
		},
		toggleSelectAllSims() {
			this.allSelected = !this.allSelected

			for (let i = 0; i < this.mobilesims.length; i++) {
				this.mobilesims[i].selected = this.allSelected
			}
		},
		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			}
		},
		getSearchParams() {
			let params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {}
			}

			if (this.searchParams) {
				params.where.createdAtFrom = this.searchParams.dateAddedFrom
					? moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD') + ' 00:00:00'
					: null
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD') + ' 23:59:59'
					: this.searchParams.dateAddedFrom
						? moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD') + ' 23:59:59'
						: null
			}

			if (this.searchParams.selectedTenant) params.where.selectedTenant = this.searchParams.selectedTenant

			if (this.searchParams.selectedAccount) params.where.selectedAccount = this.searchParams.selectedAccount

			if (!this.sortColumn) this.sortColumn = 'number'

			params.order = {
				[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
			}

			return params
		},
		reloadSims(globalSearch) {
			if (!this.loading) {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = {
						searchTerm: globalSearch
					}
				}

				if (this.searchedSims) {
					params.searchTerm = this.searchedSims
				}

				mobilesimsApi
					.getAll(params)
					.then((response) => {
						this.loading = false
						this.totalSims = response.data.count
						this.mobilesims = response.data.results
					})
					.catch((err) => {
						this.loading = false
					})
			}
		},
		deleteSim(sim) {
			if (sim) {
				this.$swal({
					title: 'Delete Mobile SIM',
					text: `Would you like to delete Mobile SIM: '${sim.number}'?`,
					showCancelButton: true,
					confirmButtonText: 'Delete',
					cancelButtonText: 'Cancel',
					showDenyButton: false,
					denyButtonText: 'Cancel'
				}).then((result) => {
					if (result.isConfirmed)
						mobilesimsApi
							.delete(sim.id)
							.then((response) => {
								if (response && response.data && response.data.success) this.reloadSims()
								else
									this.$swal({
										title: 'Error',
										icon: 'error',
										confirmButtonText: 'Confirm',
										text: response?.data?.error || 'Unknown error occurred, please contact support.'
									})
							})
							.catch(console.error)
				})
			}
		},
		routeTo(routeName) {
			router.push(routeName)
		},
		clearSelectedTenant() {
			this.searchParams.selectedTenant = ''
			this.$global.setSelectedTenant({ target: { value: '' } })
		},
		addFilterPill(name, category) {
			const pill = { name, category }
			this.pills.push(pill)
		},

		updatePills() {
			this.pills = []

			if (this.searchField) {
				this.addFilterPill(this.searchField, 'Name')
			}

			if (this.searchParams.selectedTenant) {
				this.addFilterPill(this.selectedTenantName, 'Tenant')
			}

			if (this.dates && this.dates.length) {
				this.addFilterPill(this.formatDates(this.dates[0], this.dates[1]), 'Date')
			}
		},

		removeFilterPill(pill) {
			const index = this.pills.findIndex((p) => p.name === pill.name && p.category === pill.category)
			if (index > -1) {
				this.pills.splice(index, 1)
			}

			switch (pill.category) {
				case 'Prefix':
					this.searchField = ''
					break
				case 'Tenant':
					this.searchParams.selectedTenant = ''
					break
				case 'Date':
					this.dates = []
					break
			}

			this.reloadAll()
		},
		changePage(page) {
			this.currentPage = page
			this.reloadSims()
		},
		showQRCode(sim, $event) {
			if ($event) {
				$event.stopPropagation()
				$event.preventDefault()
			}

			this.selectedSimId = sim.id
			this.showQRModal = true
		},
		async showUsage(sim, $event) {
			if ($event) {
				$event.stopPropagation()
				$event.preventDefault()
			}

			this.selectedSimId = sim.id
			this.showUsageModal = true
			this.isLoadingUsage = true

			try {
				const response = await mobilesimsApi.getUsage(sim.id)
				this.selectedSimUsage = response.data
			} catch (error) {
				console.error('Error fetching SIM usage:', error)
				this.$swal({
					title: 'Error',
					icon: 'error',
					confirmButtonText: 'OK',
					text: 'Failed to fetch SIM usage data. Please try again.'
				})
			} finally {
				this.isLoadingUsage = false
			}
		}
	}
}
</script>
